import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {injectIntl, defineMessages, FormattedMessage} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import * as BrowserPermission from '../BrowserPermission';
import * as Permission from '../../common/util/Permission';
import {safeJSONparse} from "../../common/util/safeJSONparse";
import {declareEditEntity} from '../../common/fw/MetaDataHelper';
import urlMapping from "../app/URLMapping";
import {EditorWithSubmit} from '../fw/EditorWithSubmit.react';
import { variableResolver } from '../../common/util/VariableResolver';

import config from '../../common/config';
import types from '../../common/model-api/types';
import ChannelHeader from '../channels/ChannelHeader.react';
import RetryFetchMutation from './RetryFetchMutation.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';

const messages = defineMessages({
    title: {id: 'fetchRetry.title', 'defaultMessage': 'Retry API Request'},
	sending: {id: 'fetchRetry.sending', 'defaultMessage': 'Sending…'},
	sent: {id: 'fetchRetry.sent', 'defaultMessage': 'Sent'},
	send: {id: 'fetchRetry.send', 'defaultMessage': 'Retry API Requests'},
	method: {id: 'fetchRetry.method', 'defaultMessage': 'Method'},
	url: {id: 'fetchRetry.url', 'defaultMessage': 'URL'},
	body: {id: 'fetchRetry.body', 'defaultMessage': 'Body'},
});

class FetchRetry extends React.Component {

    typeName = 'fetches';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };

    static contextTypes = {
        router: PropTypes.object,
    };

    onClickRetry = (args) => {
        const fetch = this.props.user.organisation.channel.fetch;

        return new Promise((resolve, reject) => {
            this.props.relay.commitUpdate(
                new RetryFetchMutation({
                    fetch: fetch,
                    body: args.body,
                    urlPart: args.urlPart,
                }), {onSuccess: resolve, onFailure: reject}
            );
        }).then(({retryFetch}) => {
            let path = urlMapping.getUrl('fetches', "view", retryFetch.fetch);
            this.context.router.push(path);
        });
    };

    render() {
        const data = this.props.user.organisation.channel.fetch;
        let type = types['fetches'];

        let request = {};

        if (data.request) {
            request = safeJSONparse(data.request);
        }
        let requestContent = request.requestBody;

        let editBody = true;
        let fetcherOptions = data.channelDef ? JSON.parse(data.channelDef.fetcherOptions) : {};
        let channelOptions = data.channel ? JSON.parse(data.channel.options) : {};
        let baseUrl = variableResolver(fetcherOptions['baseURL'] || '', channelOptions);
        let urlPart = request.url || '';
        if (urlPart.startsWith(config.LINK_SERVER)) {
            urlPart = urlPart.substring(config.LINK_SERVER.length);
        } else if (urlPart.startsWith(baseUrl)) {
            urlPart = urlPart.substring(baseUrl.length);
        }

        let list = {};

        list["method"] = {
            type: 'string',
            readOnly: false,
            name: this.props.intl.formatMessage(messages.method),
        };
        list["urlPart"] = {
            type: 'string',
            name: this.props.intl.formatMessage(messages.url),
            addonBefore: baseUrl,
            readOnly: false,
        };
        if (editBody) {
            list["body"] = {
                type: 'text',
                name: this.props.intl.formatMessage(messages.body),
                readOnly: editBody === false,
            };
        }

        let metadata = declareEditEntity({
            entity: "options",
            graphQL: "Options",
            name: "Options",
            fields: (types) => {
                return list;
            }
        });

        //<p><code>{request.method} {convertCleared(intl, request.url)}</code></p>


        let values = {
            method: request.method,
            urlPart: decodeURIComponent(urlPart),
            body: requestContent,
        };


        return (
            <AppPanel breadcrumbParams={[
                {type, data},
                {type: types['channels'], data: this.props.user.organisation.channel},
                {type: types['organisations'], data: this.props.user.organisation}]
            } title={this.props.intl.formatMessage(messages.title)}>

				<ChannelHeader data={this.props.user.organisation.channel} organisation={this.props.user.organisation}>
					<h3><FormattedMessage defaultMessage="Retry API request" id="fetchRetry.retryTitle" /> {data.sourceDef ? data.sourceDef.name : null}</h3>
				</ChannelHeader>

                <EditorWithSubmit
                    buttonTitle={this.props.intl.formatMessage(messages.sent)}
                    cancelable
                    data={values}
                    metadata={metadata}
                    submit={this.onClickRetry}
                    submitLoadingButtonNewConfig={{
                        progressText: this.props.intl.formatMessage(messages.sending),
                        text: this.props.intl.formatMessage(messages.send),
                    }}
                    urlMapping={urlMapping}
                >
                </EditorWithSubmit>

            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FetchRetry), (props) => {
    return props.user.organisation && props.user.organisation.channel && props.user.organisation.channel.fetch;
}), {
    initialVariables: {
        organisationId: '',
        channelId: '',
        fetchId: '',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
        }

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    channel(id: $channelId) {
                        id,
                        name,
                        organisation_id,
                        channelDef {
                            id,
                            name,
							icon
                        },
                        fetch(id: $fetchId) {
                            createdAt,
                            success,
                            id,
                            realId,
                            duration,
                            error,
                            errorType,
                            request,
                            dataPreview,
                            channel_id,
                            server,
                            channel {
                                id,
                                name,
                                options,
                            },
                            channelDef {
                                id,
                                name,
                                icon,
                                fetcherOptions,
                            },
                            organisation_id,
                            organisation {
                                id,
                                name,
                            },
                            actionDef {
                                name,
                                id
                            },
                            sourceDef {
                                name,
                                id
                            },
							user {
								id,
								firstname,
								surname,
								email,
								picture
							},
                            flow {
                                name,
                                id,
                                organisation_id,
                                realId,
                                flowDef {
        							id,
        							name,
        						},
                            },
							flowDef {
                                name,
                                id
                            },
							name
                        }
                    }
                }
            }
        `,
    },
});
